import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Space } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { ToolbarButton, ToolbarButtonProps } from './ToolbarButton';
import { useTranslation } from 'react-i18next';

export function useAddLinkModal(editor: LexicalEditor) {
  const [open, setOpen] = useState(false);
  const [isInsideLink, setIsInsideLink] = useState(false);
  const [initialUrl, setInitialUrl] = useState('');
  const [isSelectionEmpty, setIsSelectionEmpty] = useState(true);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          setIsSelectionEmpty(selection.isCollapsed());

          const node = selection.anchor.getNode();
          const parent = node.getParent();

          if ($isLinkNode(parent)) {
            setIsInsideLink(true);
            setInitialUrl(parent.getURL());
          } else {
            setIsInsideLink(false);
            setInitialUrl('');
          }
        } else {
          setIsSelectionEmpty(true);
          setIsInsideLink(false);
          setInitialUrl('');
        }
      });
    });
  }, [editor]);

  const showModal = useCallback(() => setOpen(true), []);
  const hideModal = useCallback(() => setOpen(false), []);

  const applyLink = useCallback(
    (url: string) => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      hideModal();
    },
    [editor, hideModal],
  );

  const removeLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    hideModal();
  }, [editor, hideModal]);

  return useMemo(
    () => ({
      open,
      onClick: showModal,
      onClose: hideModal,
      initialUrl,
      isInsideLink,
      onApply: applyLink,
      onRemove: removeLink,
      disabled: isSelectionEmpty,
    }),
    [
      open,
      showModal,
      hideModal,
      initialUrl,
      isInsideLink,
      applyLink,
      removeLink,
      isSelectionEmpty,
    ],
  );
}

interface AddLinkModalProps {
  open: boolean;
  initialUrl: string;
  isInsideLink: boolean;
  onApply: (url: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

function AddLinkModal({
  open,
  initialUrl,
  isInsideLink,
  onApply,
  onRemove,
  onClose,
}: AddLinkModalProps) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    if (open) setUrl(initialUrl);
  }, [initialUrl, open]);

  return (
    <Modal
      title={t('editor.link.title')}
      visible={open}
      onCancel={onClose}
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          {isInsideLink && (
            <Button danger icon={<LinkOutlined />} onClick={onRemove}>
              {t('editor.link.remove')}
            </Button>
          )}

          <Button type="primary" disabled={!url} onClick={() => onApply(url)}>
            {t('editor.link.apply')}
          </Button>
        </Space>
      }
      centered
    >
      <Input
        placeholder={t('editor.link.placeholder')}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
    </Modal>
  );
}

export interface ToolbarAddLinkButtonProps
  extends ToolbarButtonProps,
    AddLinkModalProps {}

export function ToolbarAddLinkButton(props: ToolbarAddLinkButtonProps) {
  return (
    <>
      <ToolbarButton {...props} />
      <AddLinkModal {...props} />
    </>
  );
}
